import { RollingCaptionStack } from "@/components/RollingCaptionStack";
import { TrackedRoomAudioRenderer } from "@/components/livekit/TrackedRoomAudioRenderer";
import {
  SpatiusAvatarCanvas,
  SpatiusAvatarProvider,
  useOptionalSpatiusAvatarContext,
  useSpatiusAvatarContext,
} from "@/components/spatius-avatar";
import {
  isSpatiusAvatarIdentity,
  isSpatiusProvider as isSpatiusProviderName,
  shouldSubscribeSpatiusViewerPublication,
} from "@/hooks/spatius-primary-session-policy";
import { useRollingCaptionLines } from "@/hooks/useRollingCaptionText";
import { extendedInteractionState } from "@/lib/extendedInteractionState";
import { getLiveKitRoomParticipantToken } from "@/lib/livekit-room-participant-token";
import { createLogger } from "@/lib/logger";
import { silenceSpatiusSdkAudioElementsForLipSync } from "@/lib/spatius-sdk-audio-cleanup";
import {
  SPATIUS_IDLE_RECOVERY_DELAY_MS,
  SPATIUS_IDLE_RECOVERY_RECHECK_MS,
  isSpatiusIdleRecoveryThrottled,
  shouldRecoverIdleSpatiusAvatar,
} from "@/lib/spatiusAvatarIdleRecovery";
import {
  AgentState,
  // Explicitly import AgentState
  BarVisualizer,
  VideoTrack,
  useTracks,
  useVoiceAssistant,
} from "@livekit/components-react";
import { Room, RoomEvent, Track } from "livekit-client";
import { Maximize2, X } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";

const logger = createLogger("VoiceAgent");
const AVATAR_UNAVAILABLE_MESSAGE =
  "Tutor video is unavailable. Your session will continue with audio.";

// ── Credentials for the Spatius avatar viewer room ──────────────────────

type SpatiusCredentials = {
  livekitUrl: string;
  roomName: string;
  participantToken: string;
  spatius: {
    appId: string;
    avatarId: string;
    sessionToken: string;
    region?: string;
  };
};

type FullscreenCapableElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};

type FullscreenCapableDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};

function isIOSLikeBrowser(): boolean {
  if (typeof navigator === "undefined") return false;

  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

function getNativeFullscreenElement(): Element | null {
  const doc = document as FullscreenCapableDocument;
  return document.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
}

async function requestNativeFullscreen(element: HTMLElement): Promise<boolean> {
  if (isIOSLikeBrowser()) {
    return false;
  }

  const target = element as FullscreenCapableElement;
  const request = target.requestFullscreen ?? target.webkitRequestFullscreen;
  if (!request) {
    return false;
  }

  await request.call(target);
  return true;
}

async function exitNativeFullscreen(): Promise<void> {
  if (!getNativeFullscreenElement()) {
    return;
  }

  const doc = document as FullscreenCapableDocument;
  const exit = document.exitFullscreen ?? doc.webkitExitFullscreen;
  if (exit) {
    await exit.call(document);
  }
}

function getVoiceFromLocalParticipant(room: Room): string | null {
  const localParticipant = room.localParticipant as any;
  const attrVoice = localParticipant?.attributes?.voice;
  if (typeof attrVoice === "string" && attrVoice.trim()) {
    return attrVoice.trim();
  }

  const metadata = localParticipant?.metadata;
  if (typeof metadata !== "string" || !metadata.trim()) {
    return null;
  }

  try {
    const parsed = JSON.parse(metadata);
    const voice = parsed?.voice;
    if (typeof voice === "string" && voice.trim()) {
      return voice.trim();
    }
  } catch {
    return null;
  }

  return null;
}

function getAvatarIdFromLocalParticipant(room: Room): string | null {
  const localParticipant = room.localParticipant as any;
  const attrAvatarId = localParticipant?.attributes?.avatarId;
  if (typeof attrAvatarId === "string" && attrAvatarId.trim()) {
    return attrAvatarId.trim();
  }

  const metadata = localParticipant?.metadata;
  if (typeof metadata !== "string" || !metadata.trim()) {
    return null;
  }

  try {
    const parsed = JSON.parse(metadata);
    const avatarId = parsed?.avatarId;
    if (typeof avatarId === "string" && avatarId.trim()) {
      return avatarId.trim();
    }
  } catch {
    return null;
  }

  return null;
}

async function fetchSpatiusCredentials(
  roomName: string,
  selectedVoice: string | null,
  avatarId: string | null,
  participantToken: string
): Promise<SpatiusCredentials> {
  const params = new URLSearchParams({ roomName });
  if (selectedVoice) {
    params.set("voice", selectedVoice);
  }
  if (avatarId) {
    params.set("avatarId", avatarId);
  }

  const response = await fetch(`/api/spatius/rtc-credentials?${params.toString()}`, {
    cache: "no-store",
    headers: { Authorization: `Bearer ${participantToken}` },
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Spatius credentials failed (${response.status}): ${text}`);
  }

  const creds = (await response.json()) as SpatiusCredentials;
  if (!creds?.spatius?.appId || !creds?.spatius?.sessionToken || !creds?.spatius?.avatarId) {
    throw new Error("Incomplete Spatius credentials from backend.");
  }

  return creds;
}

// ── Hook to lazily fetch and cache credentials ──────────────────────────────

function useSpatiusCredentials(room: Room, enabled: boolean) {
  const [credentials, setCredentials] = useState<SpatiusCredentials | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !room.name) {
      setCredentials(null);
      setError(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const participantToken = getLiveKitRoomParticipantToken(room);
        if (!participantToken) {
          throw new Error("LiveKit participant token is unavailable for Spatius authorization.");
        }
        const voice = getVoiceFromLocalParticipant(room);
        const avatarId = getAvatarIdFromLocalParticipant(room);
        const creds = await fetchSpatiusCredentials(room.name, voice, avatarId, participantToken);
        if (!cancelled) {
          setCredentials(creds);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : String(err);
          logger.error("Failed to fetch Spatius credentials", { error: message });
          setError(message);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [enabled, room, room.name]);

  return { credentials, error };
}

function ConnectedSpatiusAvatar({
  canvasClassName,
  minHeight,
  wrapperClassName,
  showBackground = true,
}: {
  canvasClassName: string;
  minHeight: number;
  wrapperClassName: string;
  showBackground?: boolean;
}) {
  const { isConnected } = useSpatiusAvatarContext();

  return (
    <div
      aria-hidden={!isConnected}
      className={`${wrapperClassName} transition-opacity duration-200 ${
        isConnected ? "opacity-100" : "pointer-events-none opacity-0"
      }`}
    >
      <SpatiusAvatarCanvas
        className={canvasClassName}
        minHeight={minHeight}
        showBackground={showBackground}
      />
    </div>
  );
}

/** What a parent placing the avatar itself can ask of it. */
export interface TutorAvatarHandle {
  /** The Spatius avatar id, so layout can remember each avatar's measured framing. */
  avatarId: string;
  isConnected: boolean;
  /** Her first idle frame is drawn (before the session connects, while still invisible). */
  isViewReady: boolean;
  getAvatarBounds: () => { x: number; y: number; width: number; height: number } | null;
  exportAvatarFrame: () => Promise<Blob | null>;
}

// Inside the Spatius context: applies the parent's hide choice (drawing pauses,
// the session and audio continue) and hands the parent the avatar's measurements.
function SpatiusAvatarBridge({
  renderingHidden,
  onAvatarHandle,
}: {
  renderingHidden: boolean;
  onAvatarHandle?: (handle: TutorAvatarHandle | null) => void;
}) {
  const {
    avatarId,
    exportAvatarFrame,
    getAvatarBounds,
    isConnected,
    isViewReady,
    setRenderingHidden,
  } = useSpatiusAvatarContext();

  useEffect(() => {
    setRenderingHidden(renderingHidden);
  }, [renderingHidden, setRenderingHidden]);

  useEffect(() => {
    onAvatarHandle?.({ avatarId, exportAvatarFrame, getAvatarBounds, isConnected, isViewReady });
  }, [avatarId, exportAvatarFrame, getAvatarBounds, isConnected, isViewReady, onAvatarHandle]);

  useEffect(() => () => onAvatarHandle?.(null), [onAvatarHandle]);

  return null;
}

function SpatiusAvatarLifecycleLogger({ agentState }: { agentState: AgentState }) {
  const { conversationState, recoverIdlePlayback, sdkConnectionState, status } =
    useSpatiusAvatarContext();
  const idleWarningTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleRecoveryRecheckTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastIdleRecoveryAtRef = React.useRef(0);
  const idleRecoveryInFlightRef = React.useRef(false);
  const latestStateRef = React.useRef({
    agentState,
    conversationState,
    sdkConnectionState,
    status,
  });

  useEffect(() => {
    latestStateRef.current = {
      agentState,
      conversationState,
      sdkConnectionState,
      status,
    };

    logger.info("Spatius avatar state updated", {
      agentState,
      conversationState,
      sdkConnectionState,
      status,
    });
  }, [agentState, conversationState, sdkConnectionState, status]);

  useEffect(() => {
    const clearIdleWarningTimer = () => {
      if (idleWarningTimerRef.current) {
        clearTimeout(idleWarningTimerRef.current);
        idleWarningTimerRef.current = null;
      }
      if (idleRecoveryRecheckTimerRef.current) {
        clearTimeout(idleRecoveryRecheckTimerRef.current);
        idleRecoveryRecheckTimerRef.current = null;
      }
    };

    if (
      !shouldRecoverIdleSpatiusAvatar({
        agentState,
        conversationState,
        status,
        viewerOwnsAudio: false,
      })
    ) {
      clearIdleWarningTimer();
      return;
    }

    clearIdleWarningTimer();
    idleWarningTimerRef.current = setTimeout(() => {
      logger.warn("Spatius avatar stayed idle while the tutor was speaking", {
        conversationState,
        idleDurationMs: SPATIUS_IDLE_RECOVERY_DELAY_MS,
        sdkConnectionState,
        status,
      });

      const nowMs = Date.now();
      if (
        idleRecoveryInFlightRef.current ||
        isSpatiusIdleRecoveryThrottled({
          lastRecoveryAtMs: lastIdleRecoveryAtRef.current,
          nowMs,
        })
      ) {
        logger.warn("Skipping Spatius idle avatar recovery because recovery is throttled", {
          inFlight: idleRecoveryInFlightRef.current,
          msSinceLastRecovery: lastIdleRecoveryAtRef.current
            ? nowMs - lastIdleRecoveryAtRef.current
            : null,
        });
        idleWarningTimerRef.current = null;
        return;
      }

      idleRecoveryInFlightRef.current = true;
      lastIdleRecoveryAtRef.current = nowMs;
      logger.warn("Recovering idle Spatius avatar visual playback while tutor is speaking", {
        conversationState,
        sdkConnectionState,
        status,
      });

      void recoverIdlePlayback()
        .then(() => {
          idleRecoveryRecheckTimerRef.current = setTimeout(() => {
            const latest = latestStateRef.current;
            if (
              shouldRecoverIdleSpatiusAvatar({
                agentState: latest.agentState,
                conversationState: latest.conversationState,
                status: latest.status,
                viewerOwnsAudio: false,
              })
            ) {
              logger.warn("Spatius avatar still idle after visual playback recovery", {
                conversationState: latest.conversationState,
                sdkConnectionState: latest.sdkConnectionState,
                status: latest.status,
                reconnectSkipped: true,
                reason: "avoid_mid_speech_viewer_reconnect_lip_sync_loss",
              });
            }
            idleRecoveryRecheckTimerRef.current = null;
          }, SPATIUS_IDLE_RECOVERY_RECHECK_MS);
        })
        .catch((error) => {
          logger.warn("Spatius idle avatar visual playback recovery failed", {
            error: error instanceof Error ? error.message : String(error),
          });
        })
        .finally(() => {
          idleRecoveryInFlightRef.current = false;
        });

      idleWarningTimerRef.current = null;
    }, SPATIUS_IDLE_RECOVERY_DELAY_MS);

    return clearIdleWarningTimer;
  }, [agentState, conversationState, recoverIdlePlayback, sdkConnectionState, status]);

  return null;
}

// ── LiveKit Voice Agent Component ───────────────────────────────────────────

interface VoiceAgentProps {
  room: Room;
  isRecording: boolean;
  compact?: boolean;
  requireVideoTrack?: boolean;
  inlineAvatarCanvasClassName?: string;
  inlineAvatarFrameClassName?: string;
  onAvatarConnectionChange?: (connected: boolean) => void;
  onAvatarError?: (message: string | null) => void;
  renderAudioRenderer?: boolean;
  /**
   * "external": only the avatar container, styled and placed by the parent
   * (inlineAvatarFrameClassName), with no click handler or built-in fullscreen.
   * The parent keeps this one instance mounted across its layouts.
   */
  presentation?: "default" | "external";
  /** External presentation: the scenery behind her (today's header circle). */
  externalBackground?: boolean;
  /** External presentation: hidden by the learner; drawing pauses, the session continues. */
  renderingHidden?: boolean;
  onAvatarHandle?: (handle: TutorAvatarHandle | null) => void;
}

const VoiceAgent: React.FC<VoiceAgentProps> = ({
  room,
  isRecording,
  compact = false,
  requireVideoTrack = false,
  inlineAvatarCanvasClassName,
  inlineAvatarFrameClassName,
  onAvatarConnectionChange,
  onAvatarError,
  renderAudioRenderer = true,
  presentation = "default",
  externalBackground = false,
  renderingHidden = false,
  onAvatarHandle,
}) => {
  const {
    state: rawAgentState,
    audioTrack,
    agent,
    agentTranscriptions,
    agentAttributes,
  } = useVoiceAssistant();
  const agentState = extendedInteractionState(rawAgentState, agentAttributes);
  const configuredAvatarProvider = (process.env.NEXT_PUBLIC_AVATAR_PROVIDER || "anam")
    .trim()
    .toLowerCase();
  const hasSpatiusPublisher = Array.from(room.remoteParticipants.values()).some((participant) =>
    isSpatiusAvatarIdentity(participant.identity)
  );
  const sharedSpatiusAvatar = useOptionalSpatiusAvatarContext();
  const isSpatiusProvider =
    !!sharedSpatiusAvatar || isSpatiusProviderName(configuredAvatarProvider) || hasSpatiusPublisher;
  const allVideoTracks = useTracks(
    [Track.Source.Camera, Track.Source.ScreenShare, Track.Source.Unknown],
    { onlySubscribed: !isSpatiusProvider }
  );
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [hasConversationStarted, setHasConversationStarted] = useState(false);
  const hasEverHadVideoRef = React.useRef(false);
  const hasEmittedFailureRef = React.useRef(false);
  const failureTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const spatiusContainerRef = React.useRef<HTMLDivElement | null>(null);
  const spatiusViewerRoomRef = React.useRef<Room | null>(null);
  const spatiusParticipantConnectedHandlerRef = React.useRef<((participant: any) => void) | null>(
    null
  );
  const spatiusTrackPublishedHandlerRef = React.useRef<
    ((publication: any, participant: any) => void) | null
  >(null);
  const spatiusTrackSubscribedHandlerRef = React.useRef<
    ((track: any, publication: any, participant: any) => void) | null
  >(null);

  // Fetch credentials when Spatius is active and recording
  const { credentials: spatiusCredentials, error: spatiusCredentialsError } = useSpatiusCredentials(
    room,
    isRecording && isSpatiusProvider && !sharedSpatiusAvatar
  );

  // Report credential errors
  useEffect(() => {
    if (spatiusCredentialsError) {
      onAvatarError?.(AVATAR_UNAVAILABLE_MESSAGE);
    }
  }, [spatiusCredentialsError, onAvatarError]);

  useEffect(() => {
    if (!sharedSpatiusAvatar) {
      return;
    }

    onAvatarConnectionChange?.(sharedSpatiusAvatar.isConnected);

    if (sharedSpatiusAvatar.error) {
      logger.warn("Shared tutor video session failed", {
        error: sharedSpatiusAvatar.error.message,
      });
      onAvatarError?.(AVATAR_UNAVAILABLE_MESSAGE);
      return;
    }

    if (sharedSpatiusAvatar.status === "connected") {
      onAvatarError?.(null);
    }
  }, [onAvatarConnectionChange, onAvatarError, sharedSpatiusAvatar]);

  const syncSpatiusViewerSubscriptions = useCallback((viewerRoom: Room) => {
    for (const participant of viewerRoom.remoteParticipants.values()) {
      for (const publication of participant.trackPublications.values()) {
        const shouldSubscribe = shouldSubscribeSpatiusViewerPublication({
          participantIdentity: participant.identity,
          publicationKind: publication.kind,
        });

        try {
          publication.setSubscribed(shouldSubscribe);
        } catch (error) {
          logger.warn("Failed to update Spatius viewer subscription", {
            participant: participant.identity,
            shouldSubscribe,
            error: error instanceof Error ? error.message : String(error),
          });
        }
      }
    }

    const cleanupResult = silenceSpatiusSdkAudioElementsForLipSync();
    if (cleanupResult.removedCount > 0 || cleanupResult.mutedCount > 0) {
      logger.warn("Silenced Spatius viewer SDK audio elements", {
        reason: "spatius_viewer_subscription_sync",
        viewerRoomName: viewerRoom.name || null,
        removedCount: cleanupResult.removedCount,
        elementIds: cleanupResult.elementIds,
        mutedCount: cleanupResult.mutedCount,
        mutedElementIds: cleanupResult.mutedElementIds,
      });
    }
  }, []);

  const detachSpatiusViewerListeners = useCallback(() => {
    const viewerRoom = spatiusViewerRoomRef.current;
    const participantConnectedHandler = spatiusParticipantConnectedHandlerRef.current;
    const trackPublishedHandler = spatiusTrackPublishedHandlerRef.current;
    const trackSubscribedHandler = spatiusTrackSubscribedHandlerRef.current;

    if (viewerRoom && participantConnectedHandler) {
      viewerRoom.off(RoomEvent.ParticipantConnected, participantConnectedHandler);
    }

    if (viewerRoom && trackPublishedHandler) {
      viewerRoom.off(RoomEvent.TrackPublished, trackPublishedHandler);
    }

    if (viewerRoom && trackSubscribedHandler) {
      viewerRoom.off(RoomEvent.TrackSubscribed, trackSubscribedHandler);
    }

    spatiusViewerRoomRef.current = null;
    spatiusParticipantConnectedHandlerRef.current = null;
    spatiusTrackPublishedHandlerRef.current = null;
    spatiusTrackSubscribedHandlerRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      detachSpatiusViewerListeners();
    };
  }, [detachSpatiusViewerListeners]);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // Handle Escape key to close full screen
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isFullScreen) {
        void exitNativeFullscreen().catch(() => {});
        setIsFullScreen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isFullScreen]);

  useEffect(() => {
    if (!isFullScreen) return;

    document.documentElement.classList.add("voice-agent-fullscreen-open");
    document.body.classList.add("voice-agent-fullscreen-open");

    return () => {
      document.documentElement.classList.remove("voice-agent-fullscreen-open");
      document.body.classList.remove("voice-agent-fullscreen-open");
    };
  }, [isFullScreen]);

  useEffect(() => {
    if (!isSpatiusProvider) return;

    const handleFullscreenChange = () => {
      const fullscreenElement = getNativeFullscreenElement();
      const container = spatiusContainerRef.current;
      const matchesContainer =
        !!fullscreenElement &&
        !!container &&
        (fullscreenElement === container || container.contains(fullscreenElement));
      setIsFullScreen(matchesContainer);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
    };
  }, [isSpatiusProvider]);

  // Try to find a track belonging to the agent, or any remote participant if none match specifically
  const videoTrack = isSpatiusProvider
    ? undefined
    : allVideoTracks.find(
        (track) =>
          track.participant.identity === agent?.identity &&
          track.publication?.kind === Track.Kind.Video &&
          Boolean(track.publication?.track)
      ) ||
      allVideoTracks.find(
        (track) =>
          track.participant.identity !== room.localParticipant.identity &&
          track.publication?.kind === Track.Kind.Video &&
          Boolean(track.publication?.track)
      );

  useEffect(() => {
    if (!agentTranscriptions?.length) return;
    setHasConversationStarted(true);
  }, [agentTranscriptions?.length]);

  useEffect(() => {
    if (isSpatiusProvider) return;

    if (videoTrack) {
      hasEverHadVideoRef.current = true;
      hasEmittedFailureRef.current = false;
      onAvatarError?.(null);
      onAvatarConnectionChange?.(true);
      if (failureTimerRef.current) {
        clearTimeout(failureTimerRef.current);
        failureTimerRef.current = null;
      }
      return;
    }

    onAvatarConnectionChange?.(false);

    if (failureTimerRef.current) {
      clearTimeout(failureTimerRef.current);
      failureTimerRef.current = null;
    }

    if (
      !requireVideoTrack ||
      !isRecording ||
      !hasConversationStarted ||
      hasEmittedFailureRef.current
    ) {
      return;
    }

    // Allow a short grace period for Anam to attach video, and a shorter one if video dropped mid-session.
    const timeoutMs = hasEverHadVideoRef.current ? 2500 : 8000;
    failureTimerRef.current = setTimeout(() => {
      hasEmittedFailureRef.current = true;
      onAvatarError?.(AVATAR_UNAVAILABLE_MESSAGE);
    }, timeoutMs);

    return () => {
      if (failureTimerRef.current) {
        clearTimeout(failureTimerRef.current);
        failureTimerRef.current = null;
      }
    };
  }, [
    isSpatiusProvider,
    videoTrack,
    requireVideoTrack,
    isRecording,
    hasConversationStarted,
    onAvatarConnectionChange,
    onAvatarError,
  ]);

  useEffect(() => {
    if (isRecording) return;

    setHasConversationStarted(false);
    hasEverHadVideoRef.current = false;
    hasEmittedFailureRef.current = false;
    detachSpatiusViewerListeners();
    onAvatarConnectionChange?.(false);
    onAvatarError?.(null);

    if (failureTimerRef.current) {
      clearTimeout(failureTimerRef.current);
      failureTimerRef.current = null;
    }
  }, [detachSpatiusViewerListeners, isRecording, onAvatarConnectionChange, onAvatarError]);

  useEffect(() => {
    if (isSpatiusProvider) return;
    if (!requireVideoTrack || videoTrack || !isFullScreen) return;
    setIsFullScreen(false);
  }, [isSpatiusProvider, requireVideoTrack, videoTrack, isFullScreen]);

  const handleAvatarConnected = useCallback(
    (viewerRoom: Room | null) => {
      detachSpatiusViewerListeners();

      if (viewerRoom) {
        syncSpatiusViewerSubscriptions(viewerRoom);

        const handleParticipantConnected = () => {
          syncSpatiusViewerSubscriptions(viewerRoom);
        };
        const handleTrackPublished = () => {
          syncSpatiusViewerSubscriptions(viewerRoom);
        };
        const handleTrackSubscribed = () => {
          syncSpatiusViewerSubscriptions(viewerRoom);
        };

        viewerRoom.on(RoomEvent.ParticipantConnected, handleParticipantConnected);
        viewerRoom.on(RoomEvent.TrackPublished, handleTrackPublished);
        viewerRoom.on(RoomEvent.TrackSubscribed, handleTrackSubscribed);

        spatiusViewerRoomRef.current = viewerRoom;
        spatiusParticipantConnectedHandlerRef.current = handleParticipantConnected;
        spatiusTrackPublishedHandlerRef.current = handleTrackPublished;
        spatiusTrackSubscribedHandlerRef.current = handleTrackSubscribed;
      }

      onAvatarError?.(null);
      onAvatarConnectionChange?.(true);
    },
    [
      detachSpatiusViewerListeners,
      onAvatarConnectionChange,
      onAvatarError,
      syncSpatiusViewerSubscriptions,
    ]
  );

  const handleAvatarDisconnected = useCallback(() => {
    detachSpatiusViewerListeners();
    onAvatarConnectionChange?.(false);
  }, [detachSpatiusViewerListeners, onAvatarConnectionChange]);

  const handleAvatarError = useCallback(
    (error: Error) => {
      detachSpatiusViewerListeners();
      onAvatarConnectionChange?.(false);
      logger.warn("Tutor video session failed", { error: error.message });
      onAvatarError?.(AVATAR_UNAVAILABLE_MESSAGE);
    },
    [detachSpatiusViewerListeners, onAvatarConnectionChange, onAvatarError]
  );

  const renderContent = (isFull: boolean) => (
    <>
      {videoTrack ? (
        <div
          className={
            isFull
              ? "relative rounded-full overflow-hidden border-orange-500/20 bg-black w-64 h-64 md:w-96 md:h-96 border-4 shadow-[0_0_50px_rgba(249,115,22,0.5)]"
              : inlineAvatarFrameClassName ||
                `relative rounded-full overflow-hidden border-orange-500/20 bg-black ${
                  compact
                    ? "w-24 h-24 border-2"
                    : "w-48 h-48 md:w-64 md:h-64 border-4 shadow-[0_0_30px_rgba(249,115,22,0.3)]"
                }`
          }
        >
          <VideoTrack trackRef={videoTrack} className="w-full h-full object-cover" />
        </div>
      ) : (
        !requireVideoTrack && (
          <div
            className={
              isFull
                ? "w-64 h-64 md:w-96 md:h-96"
                : compact
                  ? "w-24 h-24"
                  : "w-20 h-20 md:w-24 md:h-24"
            }
          >
            <BarVisualizer
              state={agentState}
              barCount={isFull ? 7 : compact ? 5 : 5}
              trackRef={audioTrack}
              className={`rounded-full bg-gray-800/50 shadow-md w-full h-full ${
                isFull ? "p-4" : compact ? "p-2" : "p-2"
              }`}
              options={{
                minHeight: isFull ? 20 : compact ? 8 : 8,
                maxHeight: isFull ? 60 : compact ? 24 : 16,
              }}
            />
          </div>
        )
      )}
    </>
  );
  const latestTranscription = agentTranscriptions?.[agentTranscriptions.length - 1];
  const fullscreenCaptionLines = useRollingCaptionLines({
    text: latestTranscription?.text,
    segmentId: latestTranscription?.id,
    isFinal: latestTranscription?.final,
    maxCharsPerLine: 34,
  });

  if (!isRecording) return null;

  if (presentation === "external" && !isSpatiusProvider) {
    return (
      <div className={inlineAvatarFrameClassName}>
        {videoTrack ? (
          <VideoTrack
            trackRef={videoTrack}
            className={`h-full w-full object-cover ${renderingHidden ? "invisible" : ""}`}
          />
        ) : null}
      </div>
    );
  }

  if (requireVideoTrack && !videoTrack && !isSpatiusProvider) {
    return renderAudioRenderer ? (
      <TrackedRoomAudioRenderer
        activeSession={isRecording}
        roomName={room.name || null}
        surface="voice-agent-no-video"
      />
    ) : null;
  }

  if (isSpatiusProvider && presentation === "external") {
    const externalAvatar = (
      <>
        <SpatiusAvatarLifecycleLogger agentState={agentState} />
        <SpatiusAvatarBridge renderingHidden={renderingHidden} onAvatarHandle={onAvatarHandle} />
        <ConnectedSpatiusAvatar
          canvasClassName={`h-full w-full ${inlineAvatarCanvasClassName ?? ""}`}
          minHeight={0}
          wrapperClassName={inlineAvatarFrameClassName ?? ""}
          showBackground={externalBackground}
        />
      </>
    );

    return sharedSpatiusAvatar ? (
      externalAvatar
    ) : (
      <SpatiusAvatarProvider
        appId={spatiusCredentials?.spatius.appId ?? ""}
        avatarId={spatiusCredentials?.spatius.avatarId ?? ""}
        connection={{
          url: spatiusCredentials?.livekitUrl ?? "",
          token: spatiusCredentials?.participantToken ?? "",
          roomName: spatiusCredentials?.roomName ?? "",
        }}
        sessionToken={spatiusCredentials?.spatius.sessionToken}
        enabled={!!spatiusCredentials && isRecording}
        onConnected={handleAvatarConnected}
        onDisconnected={handleAvatarDisconnected}
        onAvatarError={handleAvatarError}
      >
        {externalAvatar}
      </SpatiusAvatarProvider>
    );
  }

  if (isSpatiusProvider) {
    const useIosCssFullscreen = isFullScreen && isIOSLikeBrowser();
    const openSpatiusFullscreen = async () => {
      const container = spatiusContainerRef.current;
      if (!container) {
        setIsFullScreen(true);
        return;
      }

      try {
        if (await requestNativeFullscreen(container)) {
          setIsFullScreen(true);
          return;
        }
      } catch {
        // Fall back to CSS fullscreen mode below.
      }

      setIsFullScreen(true);
    };

    const closeSpatiusFullscreen = async () => {
      try {
        await exitNativeFullscreen();
      } catch {
        // Keep CSS fallback behavior even if native fullscreen exit fails.
      }
      setIsFullScreen(false);
    };

    // Compact views stay round, but fullscreen uses a portrait frame so the
    // avatar torso and shirt logo are not cropped out.
    const avatarWrapperClass = isFullScreen
      ? useIosCssFullscreen
        ? "relative overflow-hidden rounded-[24px] border-4 border-orange-500/25 bg-black w-[min(92vw,620px)] h-[min(66dvh,720px)] min-h-[300px] max-h-[calc(100dvh-12rem)] shadow-[0_0_50px_rgba(249,115,22,0.35)]"
        : "relative overflow-hidden rounded-[28px] border-4 border-orange-500/25 bg-black w-[min(92vw,680px)] h-[78dvh] min-h-[360px] max-h-[860px] shadow-[0_0_50px_rgba(249,115,22,0.35)]"
      : inlineAvatarFrameClassName ||
        `relative rounded-full overflow-hidden border-orange-500/20 bg-black ${
          compact
            ? "w-24 h-24 rounded-full border-2"
            : "w-40 h-40 md:w-52 md:h-52 rounded-full border-4 shadow-[0_0_30px_rgba(249,115,22,0.3)]"
        }`;

    const avatarCanvasClass = isFullScreen
      ? useIosCssFullscreen
        ? "w-full h-full [&_canvas]:scale-[0.92] [&_canvas]:translate-y-[7%]"
        : "w-full h-full [&_canvas]:scale-100 [&_canvas]:translate-y-0"
      : inlineAvatarCanvasClassName
        ? `w-full h-full ${inlineAvatarCanvasClassName}`
        : compact
          ? "absolute left-1/2 top-1/2 size-64 origin-center -translate-x-1/2 -translate-y-1/2 scale-[0.375] [&_canvas]:scale-100 [&_canvas]:translate-y-0"
          : "w-full h-full [&_canvas]:scale-[1.15] [&_canvas]:translate-y-[8%]";
    const iosCaptionLiftClass = useIosCssFullscreen
      ? "bottom-[calc(max(env(safe-area-inset-bottom),1rem)+3.25rem)] sm:bottom-[max(env(safe-area-inset-bottom),1rem)]"
      : "bottom-[max(env(safe-area-inset-bottom),1rem)]";
    const iosStatusLiftClass = useIosCssFullscreen
      ? "bottom-[calc(max(env(safe-area-inset-bottom),1rem)+8.5rem)] sm:bottom-[calc(max(env(safe-area-inset-bottom),1rem)+4.5rem)]"
      : "bottom-[calc(max(env(safe-area-inset-bottom),1rem)+4.5rem)]";

    const avatarContent = sharedSpatiusAvatar ? (
      <>
        <SpatiusAvatarLifecycleLogger agentState={agentState} />
        <ConnectedSpatiusAvatar
          canvasClassName={avatarCanvasClass}
          minHeight={0}
          wrapperClassName={avatarWrapperClass}
        />
      </>
    ) : (
      <SpatiusAvatarProvider
        appId={spatiusCredentials?.spatius.appId ?? ""}
        avatarId={spatiusCredentials?.spatius.avatarId ?? ""}
        connection={{
          url: spatiusCredentials?.livekitUrl ?? "",
          token: spatiusCredentials?.participantToken ?? "",
          roomName: spatiusCredentials?.roomName ?? "",
        }}
        sessionToken={spatiusCredentials?.spatius.sessionToken}
        enabled={!!spatiusCredentials && isRecording}
        onConnected={handleAvatarConnected}
        onDisconnected={handleAvatarDisconnected}
        onAvatarError={handleAvatarError}
      >
        <SpatiusAvatarLifecycleLogger agentState={agentState} />
        <ConnectedSpatiusAvatar
          canvasClassName={avatarCanvasClass}
          minHeight={0}
          wrapperClassName={avatarWrapperClass}
        />
      </SpatiusAvatarProvider>
    );

    const spatiusPlaceholderClass = isFullScreen
      ? compact
        ? "relative h-24 w-24 shrink-0"
        : "relative h-40 w-40 shrink-0 md:h-52 md:w-52"
      : "";
    const spatiusHostClass = isFullScreen
      ? "fixed inset-x-0 top-0 z-[9999] flex h-[100dvh] min-h-[100svh] items-center justify-center overflow-hidden bg-black/95 px-3 pb-[max(env(safe-area-inset-bottom),1rem)] pt-[max(env(safe-area-inset-top),1rem)] backdrop-blur-sm animate-in fade-in duration-200"
      : `relative flex justify-center items-center transition-transform hover:scale-105 cursor-pointer ${
          compact ? "" : "mb-4"
        }`;

    return (
      <div className={spatiusPlaceholderClass}>
        <div
          ref={spatiusContainerRef}
          className={spatiusHostClass}
          onClick={() => {
            void (isFullScreen ? closeSpatiusFullscreen() : openSpatiusFullscreen());
          }}
          role={isFullScreen ? "dialog" : "button"}
          aria-modal={isFullScreen ? true : undefined}
          tabIndex={0}
        >
          <div
            className={
              isFullScreen
                ? "flex items-center justify-center animate-in zoom-in-95 duration-200"
                : undefined
            }
            onClick={(e) => {
              if (isFullScreen) {
                e.stopPropagation();
              }
            }}
          >
            {avatarContent}

            {!isFullScreen && compact && (
              <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 bg-black/30 rounded-full transition-opacity">
                <Maximize2 className="text-white/80 w-6 h-6" />
              </div>
            )}
          </div>

          {isFullScreen && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  void closeSpatiusFullscreen();
                }}
                className="absolute right-4 top-[max(env(safe-area-inset-top),1rem)] p-2 rounded-full bg-white/10 text-white/70 hover:text-white hover:bg-white/20 transition-all sm:right-6 sm:top-6"
                aria-label="Close avatar fullscreen"
              >
                <X size={32} />
              </button>

              <div
                className={`absolute left-0 right-0 flex justify-center pointer-events-none ${iosStatusLiftClass}`}
              >
                <div className="text-white/50 text-sm font-medium">
                  {agentState === "speaking" ? "Speaking..." : "Listening..."}
                </div>
              </div>

              {fullscreenCaptionLines.length > 0 && (
                <div
                  className={`absolute left-0 right-0 z-[110] flex justify-center px-4 pointer-events-none sm:px-8 ${iosCaptionLiftClass}`}
                >
                  <div className="w-full max-w-3xl rounded-xl border border-white/10 bg-black/75 px-5 py-3 shadow-lg backdrop-blur-md transition-all duration-300 sm:px-6">
                    <RollingCaptionStack
                      lines={fullscreenCaptionLines}
                      className="text-base font-medium text-white/95 antialiased sm:text-lg md:text-xl"
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {renderAudioRenderer ? (
        <TrackedRoomAudioRenderer
          activeSession={isRecording}
          roomName={room.name || null}
          surface="voice-agent"
        />
      ) : null}
      {/* Compact / Inline View */}
      <div
        className={`relative flex justify-center items-center transition-transform hover:scale-105 cursor-pointer ${
          compact ? "" : "mb-4"
        }`}
        onClick={() => setIsFullScreen(true)}
        role="button"
        tabIndex={0}
      >
        {renderContent(false)}

        {/* Hover indicator for expansion */}
        {compact && (
          <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 bg-black/30 rounded-full transition-opacity">
            <Maximize2 className="text-white/80 w-6 h-6" />
          </div>
        )}
      </div>

      {/* Full Screen Portal */}
      {mounted &&
        isFullScreen &&
        createPortal(
          <div className="fixed inset-x-0 top-0 z-[100] flex h-[100dvh] min-h-[100svh] items-center justify-center overflow-hidden bg-black/90 px-3 pb-[max(env(safe-area-inset-bottom),1rem)] pt-[max(env(safe-area-inset-top),1rem)] backdrop-blur-sm animate-in fade-in duration-200">
            {/* Close Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsFullScreen(false);
              }}
              className="absolute right-4 top-[max(env(safe-area-inset-top),1rem)] rounded-full bg-white/10 p-2 text-white/70 transition-all hover:bg-white/20 hover:text-white sm:right-6 sm:top-6"
            >
              <X size={32} />
            </button>

            {/* Backdrop click to close */}
            <div className="absolute inset-0 -z-10" onClick={() => setIsFullScreen(false)} />

            {/* Content */}
            <div className="flex max-h-[calc(100dvh-11rem)] flex-col items-center justify-center p-6 animate-in zoom-in-95 duration-200 sm:p-8">
              {renderContent(true)}
              <div className="mt-8 text-white/50 text-sm font-medium">
                {agentState === "speaking" ? "Speaking..." : "Listening..."}
              </div>
            </div>

            {/* Subtitles Overlay */}
            {fullscreenCaptionLines.length > 0 && (
              <div className="pointer-events-none absolute bottom-[max(env(safe-area-inset-bottom),1rem)] left-0 right-0 z-[110] flex justify-center px-4 sm:px-8">
                <div className="w-full max-w-3xl rounded-xl border border-white/10 bg-black/60 px-6 py-3 shadow-lg backdrop-blur-md transition-all duration-300">
                  <RollingCaptionStack
                    lines={fullscreenCaptionLines}
                    className="text-lg font-medium text-white/90 antialiased md:text-xl"
                  />
                </div>
              </div>
            )}
          </div>,
          document.body
        )}
    </>
  );
};

export default VoiceAgent;
